// Order detail page at /orders/:id — shows full order, print button, void dialog.
import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { ArrowLeft, Printer, XCircle } from "lucide-react";

interface Serial { id: string; serialNo: string; }
interface OrderItem {
  id: string; variantId: string; quantity: number;
  unitPrice: string; discount: string; lineTotal: string;
  productName?: string | null; sku?: string | null;
  serials?: Serial[];
}
interface Order {
  id: string; code: string; status: string;
  subtotal: string; discountAmount: string; total: string;
  paidAmount: string; paymentMethod?: string | null;
  cashierEmail?: string | null; note: string;
  createdAt: string; paidAt?: string | null;
  voidedAt?: string | null;
  items?: OrderItem[];
}

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

function fmtDate(s?: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("vi-VN");
}

export function OrderDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [voidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { data: order, isLoading } = useQuery({
    queryKey: ["order", params.id],
    queryFn: () => apiGet<Order>(`/v1/orders/${params.id}`),
    enabled: !!params.id,
  });

  const printMutation = useMutation({
    mutationFn: () => {
      const wsId = localStorage.getItem("pos_workstation_id");
      const url = `/v1/orders/${params.id}/print${wsId ? `?workstation_id=${wsId}` : ""}`;
      return apiPost(url);
    },
  });

  const voidMutation = useMutation({
    mutationFn: () => apiPost(`/v1/orders/${params.id}/void`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", params.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setVoidOpen(false);
    },
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Đang tải...</div>;
  if (!order) return <div className="p-6 text-muted-foreground">Không tìm thấy đơn hàng.</div>;

  const canVoid = order.status === "paid" &&
    (user?.role === "admin" || user?.role === "manager") &&
    order.paidAt != null &&
    Date.now() - new Date(order.paidAt).getTime() < 3_600_000;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/orders" })}>
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-xl font-semibold">{order.code}</h1>
        <span className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => printMutation.mutate()} disabled={printMutation.isPending}>
            <Printer size={16} className="mr-1" /> In lại
          </Button>
          {canVoid && (
            <Button variant="destructive" size="sm" onClick={() => setVoidOpen(true)}>
              <XCircle size={16} className="mr-1" /> Huỷ đơn
            </Button>
          )}
        </span>
      </div>

      {/* Header info */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border bg-card p-4 text-sm">
        <div><span className="text-muted-foreground">Trạng thái: </span><strong>{order.status}</strong></div>
        <div><span className="text-muted-foreground">Thu ngân: </span>{order.cashierEmail ?? "—"}</div>
        <div><span className="text-muted-foreground">Thời gian: </span>{fmtDate(order.createdAt)}</div>
        <div><span className="text-muted-foreground">Thanh toán lúc: </span>{fmtDate(order.paidAt)}</div>
        <div><span className="text-muted-foreground">Phương thức: </span>{order.paymentMethod ?? "—"}</div>
        {order.note && <div><span className="text-muted-foreground">Ghi chú: </span>{order.note}</div>}
      </div>

      {/* Items */}
      <Table>
        <THead>
          <Tr><Th>Sản phẩm</Th><Th className="text-right">SL</Th><Th className="text-right">Đơn giá</Th><Th className="text-right">Thành tiền</Th></Tr>
        </THead>
        <TBody>
          {(order.items ?? []).map((item) => (
            <Tr key={item.id}>
              <Td>
                <div>{item.productName ?? item.variantId}</div>
                <div className="text-xs text-muted-foreground">{item.sku}</div>
                {item.serials?.map((s) => (
                  <div key={s.id} className="text-xs text-muted-foreground">SN: {s.serialNo}</div>
                ))}
              </Td>
              <Td className="text-right">{item.quantity}</Td>
              <Td className="text-right">{fmtVND(item.unitPrice)}</Td>
              <Td className="text-right font-semibold">{fmtVND(item.lineTotal)}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {/* Totals */}
      <div className="rounded-lg border bg-card p-4 text-sm space-y-2">
        <div className="flex justify-between"><span className="text-muted-foreground">Tạm tính</span><span>{fmtVND(order.subtotal)}</span></div>
        {parseFloat(order.discountAmount) > 0 && (
          <div className="flex justify-between"><span className="text-muted-foreground">Giảm giá</span><span>-{fmtVND(order.discountAmount)}</span></div>
        )}
        <div className="flex justify-between border-t pt-2 text-base font-bold">
          <span>Tổng cộng</span><span className="text-primary">{fmtVND(order.total)}</span>
        </div>
        <div className="flex justify-between"><span className="text-muted-foreground">Tiền khách</span><span>{fmtVND(order.paidAmount)}</span></div>
      </div>

      {/* Void dialog */}
      {voidOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold text-destructive">Huỷ đơn hàng</h2>
            <p className="text-sm text-muted-foreground">Lý do huỷ đơn là bắt buộc. Tồn kho sẽ được hoàn về.</p>
            <Input
              placeholder="Nhập lý do..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setVoidOpen(false)} className="flex-1">Thoát</Button>
              <Button
                variant="destructive"
                onClick={() => voidMutation.mutate()}
                disabled={!reason.trim() || voidMutation.isPending}
                className="flex-1"
              >
                {voidMutation.isPending ? "Đang huỷ..." : "Xác nhận huỷ"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
